import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiUrl } from '../config/api';

// CSS for the AI Orb animation
const orbStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=DM+Sans:wght@400;500&display=swap');
  
  @keyframes orbRotate {
    0% { transform: rotateX(-20deg) rotateY(0deg); }
    100% { transform: rotateX(-20deg) rotateY(360deg); }
  }
  
  @keyframes orbPulse {
    0%, 100% { opacity: 0.8; }
    50% { opacity: 1; }
  }
  
  @keyframes orbFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  .orb-container {
    perspective: 1000px;
    animation: orbFloat 4s ease-in-out infinite;
  }
  
  .orb {
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, 
      rgba(34, 197, 94, 0.4) 0%, 
      rgba(17, 153, 142, 0.3) 30%,
      rgba(102, 126, 234, 0.2) 60%,
      transparent 70%);
    position: relative;
    animation: orbRotate 20s linear infinite;
    box-shadow: 
      0 0 60px rgba(34, 197, 94, 0.3),
      0 0 100px rgba(17, 153, 142, 0.2),
      inset 0 0 60px rgba(34, 197, 94, 0.1);
  }
  
  .orb::before {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    border: 2px solid rgba(34, 197, 94, 0.3);
    background: 
      radial-gradient(circle at 70% 70%, rgba(102, 126, 234, 0.2), transparent 50%),
      radial-gradient(circle at 30% 30%, rgba(34, 197, 94, 0.3), transparent 50%);
  }
  
  .orb::after {
    content: '';
    position: absolute;
    inset: 20px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.1);
    animation: orbPulse 3s ease-in-out infinite;
  }
  
  .orb-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(34, 197, 94, 0.2);
    animation: orbPulse 3s ease-in-out infinite;
  }
  
  .orb-dots {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-image: 
      radial-gradient(circle, rgba(34, 197, 94, 0.8) 1px, transparent 1px),
      radial-gradient(circle, rgba(17, 153, 142, 0.6) 1px, transparent 1px);
    background-size: 20px 20px, 30px 30px;
    background-position: 0 0, 10px 10px;
    opacity: 0.4;
    animation: orbRotate 30s linear infinite reverse;
  }
`;

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = orbStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/auth/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, fullName })
      });
      const data = await res.json();

      if (res.ok) {
        localStorage.setItem('token', data.token);
        navigate('/dashboard');
      } else {
        setError(data.error || 'Signup failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: 'white',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0f1c 0%, #0d1525 50%, #0a0f1c 100%)',
      display: 'flex',
      fontFamily: "'DM Sans', sans-serif"
    }}>
      {/* Left Side - Branding & Orb */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        
        {/* AI Orb */}
        <div className="orb-container" style={{ position: 'relative', marginBottom: '40px' }}>
          <div className="orb">
            <div className="orb-dots" />
            <div className="orb-ring" style={{ inset: '30px' }} />
            <div className="orb-ring" style={{ inset: '50px', animationDelay: '0.5s' }} />
            <div className="orb-ring" style={{ inset: '70px', animationDelay: '1s' }} />
          </div>
        </div>
        
        {/* Branding Text */}
        <h1 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '36px',
          fontWeight: '700',
          color: 'white',
          marginBottom: '12px'
        }}>
          Join BrainForge
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '16px',
          maxWidth: '300px',
          textAlign: 'center',
          lineHeight: '1.6'
        }}>
          Start your 7-day free trial. No credit card required.
        </p>
        
        {/* Features */}
        <div style={{
          marginTop: '30px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {[
            '4 AI models working together',
            'Unlimited conversations',
            'File uploads & analysis',
            'Bring your own API keys'
          ].map((feature, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '14px'
            }}>
              <span style={{ color: '#22c55e' }}>✓</span>
              {feature}
            </div>
          ))}
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(255,255,255,0.08)',
          padding: '40px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '28px',
            fontWeight: '600',
            color: 'white',
            marginBottom: '8px'
          }}>
            Create your account
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.5)',
            marginBottom: '28px',
            fontSize: '15px'
          }}>
            Get started with your free trial
          </p>

          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '20px',
              color: '#f87171',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Doe"
                required
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#22c55e';
                  e.target.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#22c55e';
                  e.target.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#22c55e';
                  e.target.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#22c55e';
                  e.target.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{
                  marginTop: '4px',
                  width: '16px',
                  height: '16px',
                  accentColor: '#22c55e'
                }}
              />
              <label htmlFor="terms" style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '13px',
                lineHeight: '1.5'
              }}>
                I agree to the{' '}
                <Link to="/terms" style={{ color: 'white', textDecoration: 'underline' }}>
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" style={{ color: 'white', textDecoration: 'underline' }}>
                  Privacy Policy
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'wait' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Creating account...' : 'Start Free Trial'}
            </button>
          </form>

          <div style={{
            marginTop: '24px',
            textAlign: 'center',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255,255,255,0.08)'
          }}>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
              Already have an account?{' '}
              <Link to="/login" style={{
                color: '#22c55e',
                fontWeight: '600',
                textDecoration: 'none'
              }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Legal Footer */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '24px'
      }}>
        {['Terms', 'Privacy', 'Refund Policy'].map((item) => (
          <Link
            key={item}
            to={item === 'Refund Policy' ? '/refund' : `/${item.toLowerCase().replace(' ', '-')}`}
            style={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '12px',
              textDecoration: 'none'
            }}
          >
            {item}
          </Link>
        ))}
      </div>
    </div>
  );
}
